import { createContext, useContext, type ComponentType, type ReactNode } from 'react';

/**
 * Shape of the writable rich-text editor. The concrete renderer
 * (RichTextEditor) lives in features/editor and pulls 40+ editor-internal
 * siblings, so it cannot move to lib/. Registering it via this context lets
 * other features (e.g. features/document-surfaces) render an editable document
 * surface without a cross-feature import (import/no-restricted-paths). The
 * concrete renderer is mounted at app/router.tsx, mirroring
 * document-viewer-context.tsx and recording-notes-editor-context.tsx.
 *
 * This contract intentionally excludes the meeting chip-row props
 * (showAttendees/showCalendarChip/showTranscriptChip/showDiagramsChip) — those
 * chips were removed from the editor (M1 Task 6). Document surfaces render
 * their own header surfaces.
 */
export type DocumentEditorRenderer = ComponentType<{
  entityId: string;
  readOnly?: boolean;
  entityKindHint?: 'document' | 'chat' | 'canvas' | 'space';
  showTitle?: boolean;
  showActionbar?: boolean;
  /**
   * The Yjs fragment (Collaboration `field`) this editor binds to. Document
   * surfaces use this to render each surface against its own named fragment
   * (e.g. 'memos', 'summary'). Default 'prosemirror' keeps every existing
   * document caller byte-identical.
   */
  fragmentName?: string;
  offerGenerateFromSelection?: boolean;
  suspended?: boolean;
}>;

const DocumentEditorContext = createContext<DocumentEditorRenderer | null>(null);

interface DocumentEditorProviderProps {
  renderer: DocumentEditorRenderer;
  children: ReactNode;
}

export function DocumentEditorProvider({ renderer, children }: DocumentEditorProviderProps) {
  return (
    <DocumentEditorContext.Provider value={renderer}>
      {children}
    </DocumentEditorContext.Provider>
  );
}

/**
 * Returns the registered editor renderer, or `null` when no provider is mounted
 * (e.g. unit tests). Callers fall back to a skeleton when unavailable.
 */
export function useDocumentEditor(): DocumentEditorRenderer | null {
  return useContext(DocumentEditorContext);
}
